/**
 * An image that rebuilds its own `src` and `srcset` when it is given a new url.
 *
 * A bound `src` on a plain `<img>` loses to the `srcset` beside it — the browser goes on
 * picking a candidate from the old set, so the picture never changes. Owning both here
 * makes that impossible: one attribute changes, and the element derives everything from it.
 *
 * It also means a payload carries one url per image rather than one per crop. Each element
 * asks the CDN for the crop it wants, so the same image serves a square card and a wide
 * row without the data knowing either exists.
 *
 * The server still renders the `<img>` inside, so first paint needs no JavaScript.
 *
 * @example
 * <reactive-image widths="320,480,640" width="640" height="640" bind:url="image.url">
 *   <img src="…" srcset="…" sizes="320px" alt="">
 * </reactive-image>
 */

class ReactiveImage extends HTMLElement {
  static observedAttributes = ['url', 'alt'];

  /**
   * A picture, as the Ajax Product API describes one.
   *
   * The same swap the `url` attribute makes, for a caller that has the whole media object
   * and would otherwise have to take it apart to hand over two attributes.
   *
   * @param {{ src: string, alt?: string | null } | null} value
   */
  set image(value) {
    if (!value?.src) return;

    this.setAttribute('url', value.src);
    this.setAttribute('alt', value.alt ?? '');
  }

  /** @param {string} name */
  attributeChangedCallback(name) {
    // A url arriving before the element is parsed has no `img` to write to yet; the
    // connected callback picks it up.
    if (this.isConnected) this.#apply(name);
  }

  connectedCallback() {
    if (this.hasAttribute('url')) this.#apply('url');
    if (this.hasAttribute('alt')) this.#apply('alt');
  }

  /** @returns {HTMLImageElement | null} The image this element dresses. */
  get #image() {
    return this.querySelector('img');
  }

  /**
   * @param {string} name - Which attribute changed.
   */
  #apply(name) {
    const image = this.#image;
    if (!image) return;

    if (name === 'alt') {
      image.alt = this.getAttribute('alt') ?? '';
      return;
    }

    const url = this.getAttribute('url');
    if (!url) return;

    const width = Number(this.getAttribute('width')) || 0;
    const height = Number(this.getAttribute('height')) || width;
    const widths = (this.getAttribute('widths') ?? '')
      .split(',')
      .map((value) => Number(value.trim()))
      .filter(Boolean);

    const src = cropped(url, width, height);
    if (src === image.getAttribute('src')) return;

    image.src = src;
    image.srcset = widths.map((w) => `${cropped(url, w, ratioHeight(w, width, height))} ${w}w`).join(', ');
  }
}

/**
 * @param {number} candidate - The candidate's width.
 * @param {number} width - The element's width, the ratio's numerator.
 * @param {number} height - The element's height, the ratio's denominator.
 * @returns {number} That candidate's height at the same ratio.
 */
function ratioHeight(candidate, width, height) {
  if (!width || !height) return candidate;

  return Math.round((candidate * height) / width);
}

/**
 * Ask the CDN for one crop of an image.
 *
 * The parameters are set rather than appended, so a url that already carries them — every
 * url Liquid's `image_url` produces — is re-cropped instead of gaining a second set.
 *
 * @param {string} url - The image's url, from the Storefront API or `image_url`.
 * @param {number} width
 * @param {number} height
 * @returns {string}
 */
function cropped(url, width, height) {
  // Protocol-relative urls are what both Liquid and the Storefront API return.
  const absolute = new URL(url, window.location.href);
  if (width) absolute.searchParams.set('width', String(width));
  if (height) absolute.searchParams.set('height', String(height));
  if (width && height) absolute.searchParams.set('crop', 'center');

  return `${absolute.pathname}${absolute.search}`.startsWith('//')
    ? absolute.toString()
    : `//${absolute.host}${absolute.pathname}${absolute.search}`;
}

if (!customElements.get('reactive-image')) {
  customElements.define('reactive-image', ReactiveImage);
}
